/******************************************
*  Author : Riccardo Di Dato   
*  Created On : Wed Sep 28 2022
*  File : SubscriptionEvaluationExecutor.ts
*******************************************/

import { EvaluatedActionType, isProposeSubscription, ProposeSubscription } from "../../EvaluatedAction.js";
import { ClientSdkPublicInterface } from "../../../sdk/ClientSdkPublicInterface.js";
import { Logger } from "@ciaopeople/cp-shatsu/Logging";
import { CTAExecutionResult, EvaluatedActionExecutor, isDismissedCTA, NamedCtaEvaluator } from "../EvaluatedActionExecutor.js";
import { PromiseOnSteroids } from "@ciaopeople/cp-shatsu/PromiseOnSteroids";

export class SubscriptionEvaluationExecutor implements EvaluatedActionExecutor {
    public isExpectedType = isProposeSubscription;

    constructor(
        private readonly sdk: ClientSdkPublicInterface,
        private logger: Logger
    ) {

    }

    execute(evaluator: NamedCtaEvaluator, res: ProposeSubscription, data?: any): Promise<CTAExecutionResult> {
        if(res.type == EvaluatedActionType.AFTER_LOGIN_EVENTUALLY_PROPOSE_NOTIFICATIONS) {
            this.logger.debug("Proposing After login eventually subscribe?", res.data);
            return Promise.all([
                this.sdk.getMe(true),
                this.sdk.getPushService().getSubscriptionsAndCheckCurrentDevice(),
                this.sdk.getGui()
            ]).then(([
                me,
                sub,
                gui
            ]) => gui.afterLoginMessage(me.data, sub).then(y => {
                if (isDismissedCTA(y)) {
                    return CTAExecutionResult.DISMISSED;
                }
                else {
                    if (y) {
                        this.sdk.openSettingsUrl("notifications");
                    }
                    return CTAExecutionResult.COMPLETED;
                }
            }));
        }
        else if (res.type == EvaluatedActionType.PROPOSE_FOLLOW) {
            this.logger.debug("Proposing follow on topic?", res.data);
            if (res.data) {
                const resolvable = PromiseOnSteroids.getResolvable<boolean>();
                return this.sdk.getGui().then(
                    gui => {
                        return gui.suggestTopicFollow(res.data, resolvable.promise).then(answer => {
                            if (isDismissedCTA(answer)) {
                                return CTAExecutionResult.DISMISSED;
                            }
                            else {
                                if(answer) {
                                    const answ = this.sdk.follow({ url: res.data.url }, { source_element: "underfollow" }).then(resp => {
                                        return resp.status == 200;
                                    });
                                    this.logger.debug("Chaining resolvable for gui unlock");
                                    resolvable.chainResolve(answ, false);
                                    return CTAExecutionResult.COMPLETED;
                                }
                                else {
                                    resolvable.resolve(false);
                                    return CTAExecutionResult.NOT_EXECUTED;
                                } 
                            }
                        });
                    }
                );
            }
            else {
                return Promise.resolve(CTAExecutionResult.NOT_EXECUTED);
            }
        }
        else {
            if(this.sdk.webviewDetect().native_app) {
                this.logger.debug("native app propose subscription", res.data);
                return this.sdk.nativeapp.proposeSubscription().then(
                    (y) => {
                        return isDismissedCTA(y) ? CTAExecutionResult.DISMISSED : CTAExecutionResult.COMPLETED;
                    }
                );
            }
            else {
                this.logger.debug("show suggest notification subscription", res.data);
                return this.sdk.getGui().then(
                    gui => {
                        return gui.suggestPushNotificationSubscription(res.type, res.data).then(y => {
                            if (isDismissedCTA(y)) {
                                return CTAExecutionResult.DISMISSED;
                            }
                            else {
                                if(y) {
                                    this.sdk.openSettingsUrl("notifications");
                                }
                                return CTAExecutionResult.COMPLETED;
                            }
                        });
                    }
                );
            }
        }
    }
}
